// Simple Hover Zoom Implementation
class HoverZoom {
  constructor() {
    this.currentZoomOverlay = null;
    this.isMobile = this.detectMobile();
    this.isZoomed = false;
    this.init();
  }

  detectMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           ('ontouchstart' in window) || 
           (navigator.maxTouchPoints > 0) ||
           window.innerWidth <= 1024;
  }

  init() {
    console.log('HoverZoom: Initializing...');
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupHoverZoom());
    } else {
      this.setupHoverZoom();
    }
  }

  setupHoverZoom() {
    console.log('HoverZoom: Setting up zoom functionality...');
    
    // Check if zoom is enabled
    const productInfo = document.querySelector('product-info[data-zoom-trigger]');
    if (!productInfo) {
      console.log('HoverZoom: Not enabled - no data-zoom-trigger attribute found');
      return;
    }

    const zoomTrigger = productInfo.getAttribute('data-zoom-trigger');
    const mobileZoomBehavior = productInfo.getAttribute('data-mobile-zoom-behavior') || 'click';
    console.log('HoverZoom: Zoom trigger set to:', zoomTrigger);
    console.log('HoverZoom: Mobile zoom behavior set to:', mobileZoomBehavior);

    // If mobile and using native click-to-zoom, don't set up any custom zoom
    if (this.isMobile && mobileZoomBehavior === 'click') {
      console.log('HoverZoom: Mobile with native click-to-zoom - skipping custom zoom setup');
      return;
    }

    // Find all product images - target both images and their containers
    const productImages = document.querySelectorAll('.product__media img');
    const productMediaContainers = document.querySelectorAll('.product__media');
    console.log('HoverZoom: Found', productImages.length, 'product images');

    productImages.forEach((img, index) => {
      console.log(`HoverZoom: Setting up image ${index + 1}`);
      const mediaContainer = img.closest('.product__media');
      
      if (this.isMobile) {
        // Mobile/tablet: Check mobile zoom behavior setting
        if (mobileZoomBehavior === 'follow_desktop') {
          // Follow desktop setting
          if (zoomTrigger === 'hover') {
            console.log('HoverZoom: Mobile/tablet - setting up single tap zoom');
            
            // Add click listener to image only (container would cause double events)
            const clickHandler = (e) => this.handleClick(e, img);
            img.addEventListener('click', clickHandler);
            
            // Add touch event listeners for better mobile support
            const touchMoveHandler = (e) => {
              e.preventDefault();
              if (this.isZoomed) {
                this.handleTouchMove(e, img);
              }
            };
            
            img.addEventListener('touchmove', touchMoveHandler);
            
            img.style.cursor = 'pointer';
          } else {
            console.log('HoverZoom: Mobile/tablet - following desktop click to zoom');
            img.style.cursor = 'pointer';
          }
        } else {
          // Default: Use click to zoom (native Shopify behavior)
          console.log('HoverZoom: Mobile/tablet - using native click to zoom');
          img.style.cursor = 'pointer';
          return;
        }
      } else {
        // Desktop: Use the selected zoom trigger
        if (zoomTrigger === 'hover') {
          console.log('HoverZoom: Desktop - setting up hover zoom');
          img.addEventListener('mouseenter', (e) => this.handleMouseEnter(e));
          img.addEventListener('mouseleave', (e) => this.handleMouseLeave(e));
          img.addEventListener('mousemove', (e) => this.handleMouseMove(e));
          img.style.cursor = 'crosshair';
        } else {
          console.log('HoverZoom: Desktop - using click to zoom');
          img.style.cursor = 'pointer';
        }
      }
      
      img.style.pointerEvents = 'auto';
    });
  }

  // Simple click handler for mobile - toggles zoom
  handleClick(event, img) {
    event.preventDefault();
    event.stopPropagation();
    
    console.log('HoverZoom: Click - current zoom state:', this.isZoomed);
    
    if (this.isZoomed) {
      // Zoom out
      console.log('HoverZoom: Zooming out');
      this.zoomOut();
    } else {
      // Zoom in
      console.log('HoverZoom: Zooming in');
      this.zoomIn(event, img);
    }
  }

  zoomIn(event, img) {
    this.createZoomOverlay(img);
    this.isZoomed = true;
    
    // Set zoom position based on click/tap location
    if (this.currentZoomOverlay) {
      const zoomedImg = this.currentZoomOverlay.querySelector('img');
      if (zoomedImg) {
        const rect = img.getBoundingClientRect();
        let x, y;
        
        // Get coordinates from the appropriate event source
        if (event.changedTouches && event.changedTouches[0]) {
          // Touch event that has ended (touchend)
          x = event.changedTouches[0].clientX - rect.left;
          y = event.changedTouches[0].clientY - rect.top;
        } else if (event.touches && event.touches[0]) {
          // Active touch event
          x = event.touches[0].clientX - rect.left;
          y = event.touches[0].clientY - rect.top;
        } else {
          // Click event
          x = event.clientX - rect.left;
          y = event.clientY - rect.top;
        }
        
        this.updateZoomPosition(img, zoomedImg, x, y, rect);
      }
    }
  }

  zoomOut() {
    this.isZoomed = false; // Set this first to prevent race conditions
    this.cleanupZoomOverlay();
  }

  handleTouchMove(event, img) {
    if (!this.isZoomed || !this.currentZoomOverlay) return;
    
    const touch = event.touches[0];
    if (touch) {
      const zoomedImg = this.currentZoomOverlay.querySelector('img');
      if (zoomedImg) {
        const rect = img.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        this.updateZoomPosition(img, zoomedImg, x, y, rect);
      }
    }
  }

  // Desktop mouse handlers (unchanged)
  handleMouseEnter(event) {
    console.log('HoverZoom: Mouse enter');
    const img = event.target;
    this.createZoomOverlay(img);
  }

  handleMouseLeave(event) {
    console.log('HoverZoom: Mouse leave');
    this.isZoomed = false;
    this.cleanupZoomOverlay();
  }

  handleMouseMove(event) {
    if (!this.currentZoomOverlay) return;
    
    const img = event.target;
    const zoomedImg = this.currentZoomOverlay.querySelector('img');
    if (!zoomedImg) return;

    const rect = img.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    this.updateZoomPosition(img, zoomedImg, x, y, rect);
  }

  updateZoomPosition(img, zoomedImg, x, y, rect) {
    // Calculate the percentage position within the image
    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;

    // Use 3x zoom factor
    const zoomFactor = 3;
    const translateX = -(xPercent * zoomFactor - 50);
    const translateY = -(yPercent * zoomFactor - 50);

    zoomedImg.style.transform = `translate(${translateX}%, ${translateY}%)`;
  }

  createZoomOverlay(img) {
    // Remove existing overlay if any
    this.cleanupZoomOverlay();

    // Create zoom overlay
    const zoomOverlay = document.createElement('div');
    zoomOverlay.className = 'zoom-overlay';
    zoomOverlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s ease;
      z-index: 5;
      border-radius: inherit;
    `;

    const zoomedImg = document.createElement('img');
    zoomedImg.src = img.src;
    zoomedImg.style.cssText = `
      width: 300%;
      height: 300%;
      object-fit: cover;
      transform-origin: center center;
      transition: transform 0.1s ease;
    `;

    zoomOverlay.appendChild(zoomedImg);

    // Ensure parent has relative positioning
    const parentElement = img.parentElement;
    if (getComputedStyle(parentElement).position === 'static') {
      parentElement.style.position = 'relative';
    }

    parentElement.appendChild(zoomOverlay);

    // Add click listener to the zoom overlay for zoom out
    zoomOverlay.addEventListener('click', (e) => this.handleClick(e, img));
    
    // Add click listener to document body to zoom out when clicking outside
    const bodyClickHandler = (e) => {
      // Check if click is outside the product media container
      const mediaContainer = img.closest('.product__media');
      if (mediaContainer && !mediaContainer.contains(e.target)) {
        console.log('HoverZoom: Click outside image container - zooming out');
        this.zoomOut();
        document.body.removeEventListener('click', bodyClickHandler);
      }
    };
    document.body.addEventListener('click', bodyClickHandler);

    // Show zoom overlay
    setTimeout(() => {
      zoomOverlay.style.opacity = '1';
      zoomOverlay.classList.add('active');
    }, 10);

    this.currentZoomOverlay = zoomOverlay;
  }

  cleanupZoomOverlay() {
    if (this.currentZoomOverlay) {
      // Force immediate removal without transition for better UX
      if (this.currentZoomOverlay.parentElement) {
        this.currentZoomOverlay.parentElement.removeChild(this.currentZoomOverlay);
      }
      this.currentZoomOverlay = null;
    }
    // Don't set isZoomed here, let the calling method handle it
  }
}

// Initialize hover zoom when the script loads
console.log('HoverZoom: Script loaded');
const hoverZoom = new HoverZoom();